import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, PLATFORM_ID, computed, inject, signal } from '@angular/core';

import { EstadoNotificacionesNavegador, NotificacionesNavegadorService } from 'src/app/services/notificaciones-navegador/notificaciones-navegador.service';
import { SessionService } from 'src/app/services/session/session.service';

/** Dónde se guarda hasta cuándo está callado el aviso (marca de tiempo en ms). */
export const CLAVE_AVISO_NOTIFICACIONES = 'mc_aviso_notificaciones_hasta';
/** Cerrarlo lo calla unos 30 días. */
export const SILENCIO_AVISO_MS = 30 * 24 * 60 * 60 * 1000;
/** No sale en el primer pintado: espera a que la página ya esté a la vista. */
export const RETRASO_AVISO_MS = 4000;

/**
 * Aviso suave «¿Te avisamos de los chollos al momento?» (02/11/2026). Desde 9acdcc7f la web
 * ya no pide el permiso al cargar (Chrome lo bloquea u oculta sin un gesto del usuario), así
 * que nadie veía nunca la pregunta. Esto es un aviso de la página —no el diálogo del
 * navegador— para usuarios con sesión y el permiso aún sin decidir; el diálogo nativo sólo
 * sale al pulsar «Activar», dentro de ese toque. Se cierra y no vuelve en 30 días.
 */
@Component({
  selector: 'app-aviso-notificaciones',
  templateUrl: './aviso-notificaciones.component.html',
  styleUrls: ['./aviso-notificaciones.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvisoNotificacionesComponent {
  private session = inject(SessionService);
  private notificaciones = inject(NotificacionesNavegadorService);
  private readonly navegador = isPlatformBrowser(inject(PLATFORM_ID));

  /** Pasó el retraso inicial (nunca en el servidor). */
  private readonly listo = signal(false);
  private readonly permiso = signal<EstadoNotificacionesNavegador>('unsupported');
  private readonly cerrado = signal(false);
  readonly activando = signal(false);

  readonly visible = computed(() =>
    this.listo() && !this.cerrado() && !!this.session.user() && this.permiso() === 'default');

  constructor() {
    if (!this.navegador) return;
    const espera = setTimeout(() => {
      this.permiso.set(this.notificaciones.permiso());
      this.cerrado.set(this.silenciado());
      this.listo.set(true);
    }, RETRASO_AVISO_MS);
    inject(DestroyRef).onDestroy(() => clearTimeout(espera));
  }

  /** El diálogo del navegador se pide aquí, en el toque; diga lo que diga, el aviso se va. */
  async activar() {
    if (this.activando()) return;
    this.activando.set(true);
    try {
      this.permiso.set(await this.notificaciones.activar());
    } finally {
      this.activando.set(false);
      if (this.permiso() === 'default') this.cerrar();
    }
  }

  cerrar() {
    this.cerrado.set(true);
    try {
      localStorage.setItem(CLAVE_AVISO_NOTIFICACIONES, String(Date.now() + SILENCIO_AVISO_MS));
    } catch { /* sin almacenamiento (modo privado): se calla sólo en esta visita */ }
  }

  private silenciado(): boolean {
    try {
      const hasta = Number(localStorage.getItem(CLAVE_AVISO_NOTIFICACIONES));
      return Number.isFinite(hasta) && hasta > Date.now();
    } catch {
      return false;
    }
  }
}
